import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Grid, TextField } from '@mui/material';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';
import { addContract, editContract } from '../../../../api/endpoints/cmsSite';
import type { Contract } from '../../../../api/types/cmsSite';
import { FormDialog } from '../../../../components/common';

const schema = z.object({
  title: z.string().min(1, 'Başlık gerekli'),
  content: z.string().optional(),
  type: z.string().optional(),
});

type FormValues = z.infer<typeof schema>;

const emptyValues: FormValues = { title: '', content: '', type: '' };

function toFormValues(contract: Contract): FormValues {
  return {
    title: contract.title ?? '',
    content: contract.content ?? '',
    type: contract.type ?? '',
  };
}

/** Create/edit dialog for Sözleşmeler (Site Contract). Confirmed via local-backend swagger
 *  + live curl (see api/types/cmsSite.ts header). `type` has no observed live examples for
 *  this resource (unlike About's 4 observed values) — modeled as a plain free-text field.
 *  Always rendered inside a specific site's tab, so `site_Id` is injected from the `siteId`
 *  prop. */
export function ContractFormDialog({
  open,
  contract,
  siteId,
  onClose,
}: {
  open: boolean;
  /** null = create mode; otherwise edit mode for this row. */
  contract: Contract | null;
  siteId: number;
  onClose: () => void;
}) {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();
  const isEdit = !!contract;

  const {
    handleSubmit,
    register,
    reset,
    formState: { errors },
  } = useForm<FormValues>({ resolver: zodResolver(schema), defaultValues: emptyValues });

  useEffect(() => {
    if (open) reset(contract ? toFormValues(contract) : emptyValues);
  }, [open, contract, reset]);

  const mutation = useMutation({
    mutationFn: (values: FormValues) =>
      isEdit && contract
        ? editContract({ id: contract.id, site_Id: siteId, ...values })
        : addContract({ ...values, site_Id: siteId }),
    onSuccess: () => {
      enqueueSnackbar(isEdit ? 'Sözleşme güncellendi.' : 'Sözleşme oluşturuldu.', { variant: 'success' });
      queryClient.invalidateQueries({ queryKey: ['cms', 'contracts'] });
      onClose();
    },
    onError: () => {
      /* surfaced via ApiErrorAlert below */
    },
  });

  const submit = handleSubmit((values) => mutation.mutate(values));

  return (
    <FormDialog
      open={open}
      title={isEdit ? 'Sözleşmeyi Güncelle' : 'Yeni Sözleşme'}
      onClose={onClose}
      onSubmit={submit}
      submitting={mutation.isPending}
      error={mutation.error}
    >
      <Grid container spacing={2}>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                size="small"
                label="Başlık"
                error={!!errors.title}
                helperText={errors.title?.message}
                {...register('title')}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField fullWidth size="small" label="Tip" {...register('type')} />
            </Grid>
            <Grid size={12}>
              <TextField fullWidth size="small" multiline rows={5} label="İçerik" {...register('content')} />
            </Grid>
      </Grid>
    </FormDialog>
  );
}
